import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

import SpinnerButton from '@/components/common/SpinnerButton';
import Input from '@/components/form/Input';
import { Textarea } from '@/components/ui/textarea';
import Error from '@/components/common/Error';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';

import { usePutCoachSelf, useCoachSelf } from '@/data/coach';

const formSchema = z.object({
  preferredName: z.string().optional().nullable(),
  bioShort: z
    .string()
    .min(1, { message: 'Short bio is required' })
    .max(500, { message: 'Short bio must be 500 characters or less' })
    .optional(),
  bioFull: z.string().optional()
});

const ProfileForm = ({ onSuccess }) => {
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const putCoach = usePutCoachSelf();
  const coachSelf = useCoachSelf();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      preferredName: coachSelf.data?.preferredName || '',
      bioShort: coachSelf.data?.bioShort || '',
      bioFull: coachSelf.data?.bioFull || ''
    }
  });

  const onSubmit = async (values) => {
    try {
      setSubmitting(true);
      setApiError('');
      const payload = {
        ...values,
        preferredName: values.preferredName || null
      };
      const updatedCoach = await putCoach(payload);
      await coachSelf.mutate(updatedCoach, false);
      await onSuccess();
    } catch (err) {
      setApiError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="preferredName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Preferred Name</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="bioShort"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Short Bio</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="A brief introduction (max 500 characters)"
                  style={{ height: 125 }}
                />
              </FormControl>
              <FormMessage />
              <FormDescription>
                A brief introduction (max 500 characters)
              </FormDescription>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="bioFull"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Full Bio</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Enter your full bio"
                  style={{ height: 250 }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {apiError && <Error error={apiError} />}
        <div className="flex justify-end mt-4">
          <SpinnerButton loading={submitting}>Save Changes</SpinnerButton>
        </div>
      </form>
    </Form>
  );
};

export default ProfileForm;
